import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle, ExternalLink } from 'lucide-react';
interface ConsentGateProps {
  onConsent: (agreed: boolean) => void;
  language: 'en' | 'vi';
}
export const ConsentGate: React.FC<ConsentGateProps> = ({
  onConsent,
  language
}) => {
  const [agreed, setAgreed] = useState(false);
  const translations = {
    en: {
      title: "Welcome to Trung Nguyen's Portfolio",
      message: 'We collect data for research purposes to improve your experience.',
      agreeButton: 'I Agree',
      declineText: 'Decline',
      learnMore: 'Learn more about data collection',
      consentText: 'By clicking "I Agree", you consent to data collection as described in our privacy policy.'
    },
    vi: {
      title: 'Chào Mừng Đến Portfolio Của Trung Nguyễn',
      message: 'Chúng tôi thu thập dữ liệu cho mục đích nghiên cứu để cải thiện trải nghiệm của bạn.',
      agreeButton: 'Tôi Đồng Ý',
      declineText: 'Từ Chối',
      learnMore: 'Tìm hiểu thêm về thu thập dữ liệu',
      consentText: 'Bằng cách nhấp "Tôi Đồng Ý", bạn đồng ý với việc thu thập dữ liệu như được mô tả trong chính sách bảo mật của chúng tôi.'
    }
  };
  const t = translations[language];
  const handleAgree = () => {
    onConsent(true);
  };
  const handleDecline = () => {
    // Redirect to Google or show a message
    window.location.href = 'https://google.com';
  };
  const handleLearnMore = () => {
    // Open privacy policy in new tab
    window.open('/privacy-policy', '_blank');
  };
  return <AnimatePresence data-id="element-274">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/70 backdrop-blur-md p-4" data-id="element-275">
        <motion.div initial={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-200 dark:border-gray-700" data-id="element-276">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-center" data-id="element-277">
            <div className="bg-white/20 p-4 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center" data-id="element-278">
              <Shield className="w-10 h-10 text-white" data-id="element-279" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2" data-id="element-280">{t.title}</h2>
          </div>

          {/* Content */}
          <div className="p-8" data-id="element-281">
            <p className="text-gray-700 dark:text-gray-300 text-center text-lg mb-6 leading-relaxed" data-id="element-282">
              {t.message}
            </p>

            {/* Consent Checkbox */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6 border border-blue-200 dark:border-blue-800" data-id="element-283">
              <label className="flex items-start gap-3 cursor-pointer group" data-id="element-284">
                <div className="relative flex-shrink-0 mt-1" data-id="element-285">
                  <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="peer sr-only" data-id="element-286" />
                  <div className="w-6 h-6 border-2 border-blue-400 dark:border-blue-500 rounded-md peer-checked:bg-blue-600 peer-checked:border-blue-600 transition-all flex items-center justify-center" data-id="element-287">
                    {agreed && <CheckCircle className="w-5 h-5 text-white" data-id="element-288" />}
                  </div>
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors" data-id="element-289">
                  {t.consentText}
                </span>
              </label>
            </div>

            {/* Agree Button */}
            <button onClick={handleAgree} disabled={!agreed} className={`w-full py-4 px-6 rounded-xl text-white font-bold text-lg shadow-lg transition-all mb-3 ${agreed ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:scale-105 active:scale-95' : 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed opacity-50'}`} data-id="element-290">
              {t.agreeButton}
            </button>

            {/* Learn More Link */}
            <button onClick={handleLearnMore} className="flex items-center justify-center gap-2 text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium mb-3 w-full" data-id="element-291">
              {t.learnMore}
              <ExternalLink size={14} data-id="element-292" />
            </button>

            {/* Decline Text */}
            <button onClick={handleDecline} className="w-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-sm transition-colors" data-id="element-293">
              {t.declineText}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
};